import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";

const ACCESS_KEY = "Arjunaspace";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-white/5 px-4 py-3 pl-11 text-white placeholder:text-slate-500 focus:border-arjuna-teal focus:outline-none focus:ring-1 focus:ring-arjuna-teal/40 transition-colors duration-300";

export default function AccessKey() {
  const { user, accessGranted, grantAccess } = useAuth();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
    else if (accessGranted) navigate("/portal", { replace: true });
  }, [user, accessGranted, navigate]);

  const submit = (e) => {
    e.preventDefault();
    setLoading(true);
    // brief delay for feedback
    setTimeout(() => {
      if (value.trim().toLowerCase() === ACCESS_KEY.toLowerCase()) {
        grantAccess();
        toast.success("Access granted.");
        navigate("/portal");
      } else {
        toast.error("Invalid access key. Please try again.");
        setLoading(false);
      }
    }, 500);
  };

  return (
    <AuthShell
      title="Enter access key"
      subtitle="This workspace is restricted. Enter your access key to continue."
    >
      <form onSubmit={submit} className="space-y-6" data-testid="access-key-form">
        <div>
          <label className="overline mb-2 block">Access key</label>
          <div className="relative">
            <KeyRound className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-arjuna-teal" />
            <input
              data-testid="access-key-input"
              type="password"
              autoFocus
              required
              className={inputCls}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="••••••••••"
            />
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="access-key-submit" className="btn-primary w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          {loading ? "Verifying…" : "Unlock workspace"}
        </button>
        <p className="flex items-center gap-2 font-mono text-xs text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-arjuna-teal" />
          Signed in as {typeof user === "object" ? user?.email : "…"}
        </p>
      </form>
    </AuthShell>
  );
}
